/* eslint-disable prettier/prettier */
import { type ECharts, type SeriesModel } from 'echarts';
import { util } from 'echarts/core';
import { type PathState } from 'zrender';
import Element, {
  ElementProps,
  ElementStatePropNames,
  ElementAnimateConfig
} from 'zrender/lib/Element';
import type StandardChart from '../core/StandardChart';
import { equalsArray, normalizeToArray } from '../util/utils';

// 标记显隐动画支持的字段
export type ElemmentStateProps = Pick<ElementProps, ElementStatePropNames> & PathState;

// 标记包含的状态
interface ElementStates {
  hide: ElemmentStateProps;
  show: ElemmentStateProps;
}

export interface StateTransitionConfig {
  stateAnimationConfig?: ElementAnimateConfig;
  states: ElementStates;
}

export type ModelAndView = {
  model: SeriesModel;
  view: unknown;
};

type MarkState = {
  state: 'show' | 'hide';
};

type MarkStates = {
  mark: (MarkState & { categoryIndex: number })[];
};

type StateTransitionHandler = (
  // 本次状态转移的目标状态
  state: 'show' | 'hide',
  // 当前x坐标的前一个标记在自定义标记系列的索引
  markIndex: number,
  // 当前自定义标记系列在标记状态列表中的索引
  markerIndex: number,
  // 传入的x坐标索引
  categoryIndex: number,
  // 整个图表的标记状态列表
  markerStates: MarkStates[]
) => number[];

const defaultStateTransitionConfig: StateTransitionConfig = {
  states: {
    hide: {
      y: 10,
      style: {
        opacity: 0
      }
    },
    show: {
      style: {
        opacity: 1
      }
    }
  },
  stateAnimationConfig: {
    duration: 250,
    delay: 0
  }
};

const defaultShowHandler: StateTransitionHandler = (_, markIndex) => {
  const showArr: number[] = [];
  if (markIndex !== -1) {
    for (let i = 0; i <= markIndex; i += 1) {
      showArr.push(i);
    }
  }
  return showArr;
};

const streamHideHandler: StateTransitionHandler = (
  state,
  markIndex,
  markerIndex,
  categoryIndex,
  markerStates
) => {
  const { mark } = markerStates[markerIndex];
  let hideList: number[] = [];
  const preShowList: number[] = [];
  mark.forEach((item, idx) => {
    if (item.state === 'show') {
      preShowList.push(idx);
    }
  });
  // console.log('前状态', preShowList, '现状态', markIndex, '列表', this.markerStates);
  if (!equalsArray(preShowList, normalizeToArray(markIndex)) && state === 'show') {
    hideList = preShowList.filter(item => {
      // 默认按照流水线显示 则隐藏除当前状态下显示的标记，其余全部隐藏
      return normalizeToArray(markIndex).indexOf(item) === -1;
    });
    // console.log('带隐藏列表', hideList);
  }
  return hideList;
};
function initMarker(
  markerModelsAndViews: ModelAndView[],
  stateTransitionCfg: StateTransitionConfig
) {
  markerModelsAndViews.forEach(item => {
    const { view } = item;
    const { group } = view;
    group.eachChild((element: Element) => {
      element.extra = {
        stateElArr: []
      };

      element.traverse(el => {
        if (!el.isGroup) {
          el.states.hide = stateTransitionCfg.states.hide;
          el.states.show = stateTransitionCfg.states.show;
          el.stateTransition = stateTransitionCfg.stateAnimationConfig;
          (element.extra.stateElArr as Element[]).push(el);
          el.useState('hide', false, true, false);
        }
      });
    });
  });
}

export class DynamicDvMarker {
  private _Chart: StandardChart;

  myChart: ECharts;

  markerModelsAndViews: ModelAndView[];

  private markerStates: MarkStates[];

  StateTransitionAnimationConfig: StateTransitionConfig;

  constructor(chart: StandardChart, config?: StateTransitionConfig) {
    this._Chart = chart;
    this.StateTransitionAnimationConfig = config || defaultStateTransitionConfig;
    this.myChart = this._Chart.getECharts();
    this.init();
  }

  private init() {
    const { myChart } = this;
    const ecModel = myChart.getModel();
    const markerModels = ecModel
      .getSeriesByType('custom')
      .filter(item => item.option?.dvType === 'dvMarker');
    this.markerModelsAndViews = markerModels.map((item: SeriesModel) => {
      return {
        model: item,
        view: myChart.getViewOfSeriesModel(item)
      };
    });
    this.markerStates = this.markerModelsAndViews.map(element => {
      const { data } = element.model.option;
      const len = data.length;
      return {
        mark: Array(len)
          .fill(0)
          .map((item, index) => {
            return {
              state: 'hide',
              categoryIndex: data[index].value[0] as number
            };
          })
          .sort((a, b) => a.categoryIndex - b.categoryIndex)
      };
    });
    initMarker(this.markerModelsAndViews, this.StateTransitionAnimationConfig);
  }

  updateMarkState() {
    const { markerModelsAndViews, markerStates } = this;
    markerModelsAndViews.forEach((element, viewIndex) => {
      const { view } = element;
      const { group } = view;
      const { mark } = markerStates[viewIndex];
      group.eachChild((item: Element, markIndex: number) => {
        const { state } = mark[markIndex];
        (item.extra.stateElArr as Element[]).forEach(el => {
          el.useState(state);
        });
      });
    });
  }

  setMarkState(markerIndex: number, markIndex: number, state: 'show' | 'hide') {
    const { markerStates } = this;
    markerStates[markerIndex].mark[markIndex].state = state;
    this.updateMarkState();
  }

  /**
   *
   * @param state 要转换的状态
   * @param markerIndex 当前marker系列在所有marker系列中的序号
   * @param categoryIndex x轴的索引
   * @param showHandler 当前marker系列哪些需要显示的标记的处理函数,返回一个标记的索引数组
   * @param hideHandler 当前marker系列哪些需要隐藏的标记的处理函数,返回一个标记的索引数组
   * @returns
   */

  showOrHide(
    state: 'hide' | 'show',
    markerIndex: number,
    categoryIndex: number,
    showHandler: StateTransitionHandler = defaultShowHandler,
    hideHandler: StateTransitionHandler = streamHideHandler
  ): number {
    const { markerStates } = this;
    const { mark } = markerStates[markerIndex];
    let markIndex: number;
    if (state === 'hide') {
      markIndex = mark.findIndex(item => item.categoryIndex === categoryIndex);
    } else {
      markIndex = mark.findIndex((item, index) => {
        return (
          item.categoryIndex <= categoryIndex &&
          ((mark[index + 1] && mark[index + 1].categoryIndex > categoryIndex) || !mark[index + 1])
        );
      });
    }

    if (util.isFunction(showHandler)) {
      const showArr = showHandler(
        state,
        markIndex,
        markerIndex,
        categoryIndex,
        util.clone(markerStates)
      );
      showArr.forEach(item => {
        this.setMarkState(markerIndex, item, 'show');
      });
    }

    if (util.isFunction(hideHandler)) {
      const hideArr = hideHandler(
        state,
        markIndex,
        markerIndex,
        categoryIndex,
        util.clone(markerStates)
      );
      hideArr.forEach(item => {
        this.setMarkState(markerIndex, item, 'hide');
      });
    }

    return markIndex;
  }

  /**
   * 显示某个x轴序号上的所有标记
   * @param categoryIndex x轴索引
   * @param isStream 是否开启流水线式显示，即下一个标记显示之前，隐藏上一个标价，最后一个标记显示时，全部标记都显示.
   * @param all 是否显示全部
   * @default
   * isStream = true
   * all = false
   */
  show(
    categoryIndex?: number,
    isStream = true,
    all = false,
    showHandler?: StateTransitionHandler,
    hideHandler?: StateTransitionHandler
  ) {
    const { markerModelsAndViews, markerStates } = this;
    markerModelsAndViews.forEach((item, index) => {
      let showFunc: StateTransitionHandler | undefined = showHandler;
      let hideFunc: StateTransitionHandler | undefined = hideHandler;
      if (typeof showFunc === 'undefined' && typeof hideFunc === 'undefined') {
        if (all) {
          showFunc = () => markerStates[index].mark.map((_, idx: number) => idx);
          hideFunc = () => [];
        } else if (isStream) {
          showFunc = undefined;
          hideFunc = undefined;
        } else if (!isStream) {
          showFunc = (_, markIndex) =>
            markerStates[index].mark
              .map((_, idx: number) => idx)
              .filter(value => value <= markIndex);
          hideFunc = () => [];
        }
      }
      this.showOrHide('show', index, categoryIndex, showFunc, hideFunc);
    });
  }
}
